import axios from 'axios';

/**
 * Single axios instance shared by every domain API module.
 *
 * Authentication is carried by the `cwr_auth` httpOnly cookie the backend
 * sets on successful login. We do not touch the token from JS at all — the
 * browser attaches it automatically as long as `withCredentials: true` is
 * set and the CORS origin/credentials policy matches. This gives us:
 *
 *   1. Refresh persistence (cookie survives page reload)
 *   2. Deep-link persistence (cookie sent on top-level GET navigation thanks
 *      to SameSite=Lax)
 *   3. XSS resistance (JS cannot read or exfiltrate the token)
 *   4. Free CSRF protection on mutations (SameSite=Lax blocks cross-site
 *      POST/PUT/PATCH/DELETE)
 */
const apiClient = axios.create({
  baseURL: '/api',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor — on 401 during a normal request, bounce to /login.
// We intentionally do NOT bounce on 401s from /auth/me itself, because that
// call IS the "am I logged in?" check and a 401 there is the expected "no"
// answer during app boot.
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url ?? '';
      const isMeProbe = url.includes('/auth/me');
      const alreadyOnLogin = window.location.pathname.startsWith('/login');
      if (!isMeProbe && !alreadyOnLogin) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  },
);

export default apiClient;
